// 伊豆1st の温泉は成分調査で供給停止中、再開時期未定。
// 拠点 伊豆1st の予約全件で温泉料金・入湯税の免除ルールへの必須同意を求める。
//
// zh-CN / zh-TW の翻訳はレビュー未着のため、暫定で en 文言をそのまま表示している。
// レビュー到着後は locales/{zh-CN,zh-TW}/component/reservation-form.json の
// specialTerms.izu1stOnsenSuspension を差し替え。
import type { ReactElement } from "react";
import { useCallback, useState } from "react";

import {
  ReservationAgreeButton,
  type ReservationAgreeButtonMessages,
} from "../../ui/buttons/reservation-agree-button";
import { RequiredAnswerSection } from "../required-answer-section";

export type Izu1stOnsenSuspensionTermsMessages = {
  title: string;
  paragraph1: string;
  period: string;
  periodNote: string;
  paragraphForRay: string;
  paragraphForSowa: string;
  unavailableHeading: string;
  unavailableBody: string;
  unavailableOwner: string;
  availableHeading: string;
  availableBody: string;
  agreeButtonLabel: string;
  agreeButtonMessages?: ReservationAgreeButtonMessages;
  requiredLabel?: string;
};

const JA_MESSAGES: Izu1stOnsenSuspensionTermsMessages = {
  title: "温泉供給一時停止のお知らせ",
  paragraph1: "伊豆1stの温泉は、成分調査のため供給を一時停止しています。",
  period: "対象期間：10/8チェックイン〜10/25チェックインまで",
  periodNote: "※期間は前後する可能性があります。",
  paragraphForRay: "期間中のお風呂は、沸かし湯でご利用いただけます。",
  paragraphForSowa:
    "期間中のお風呂は、シャワールームのホースで沸かし湯をためてご利用いただけます。",
  unavailableHeading: "当日客室の温泉をご利用いただけない場合",
  unavailableBody:
    "温泉利用料（5,500円/泊）および入湯税のご負担はございません。",
  unavailableOwner:
    "※伊豆1stのオーナーさまへは、泊数は返還され、入湯税のご負担なしとなります。",
  availableHeading: "当日客室の温泉をご利用いただける場合",
  availableBody: "温泉利用料・入湯税負担、泊数利用の対象となります。",
  agreeButtonLabel: "上記の内容を確認しました",
};

export function useIzu1stOnsenSuspensionTerms(
  siteSlug: string,
  seriesShortName: string,
  messages: Izu1stOnsenSuspensionTermsMessages = JA_MESSAGES,
): {
  renderIzu1stOnsenSuspensionTerms: () => ReactElement | null;
  agreedOrUnnecessary: boolean;
} {
  const [agreed, setAgreed] = useState(false);
  const isRequired = siteSlug === "izu1st";

  const renderIzu1stOnsenSuspensionTerms = useCallback(() => {
    if (!isRequired) {
      return null;
    }
    return (
      <Izu1stOnsenSuspensionTerms
        agreed={agreed}
        setAgreed={setAgreed}
        seriesShortName={seriesShortName}
        messages={messages}
      />
    );
  }, [agreed, isRequired, messages, seriesShortName]);

  return {
    renderIzu1stOnsenSuspensionTerms,
    agreedOrUnnecessary: agreed || !isRequired,
  };
}

export function Izu1stOnsenSuspensionTerms({
  agreed,
  setAgreed,
  seriesShortName,
  messages = JA_MESSAGES,
}: {
  agreed: boolean;
  setAgreed: (value: boolean) => void;
  seriesShortName: string;
  messages?: Izu1stOnsenSuspensionTermsMessages;
}) {
  const isSowa = seriesShortName === "SOWA";
  return (
    <RequiredAnswerSection
      title={messages.title}
      messages={
        messages.requiredLabel
          ? { requiredLabel: messages.requiredLabel }
          : undefined
      }
      actionArea={
        <div className="flex justify-center">
          <ReservationAgreeButton
            label={messages.agreeButtonLabel}
            required
            checked={agreed}
            onToggle={setAgreed}
            testId="izu1st-onsen-suspension-agree-button"
            messages={messages.agreeButtonMessages}
          />
        </div>
      }
    >
      <p>{messages.paragraph1}</p>
      <p className="mt-[1em]">{messages.period}</p>
      <p>{messages.periodNote}</p>
      <p className="mt-[1em]">
        {isSowa ? messages.paragraphForSowa : messages.paragraphForRay}
      </p>
      <p className="mt-[1em] font-bold">{messages.unavailableHeading}</p>
      <p className="mt-[0.5em]">{messages.unavailableBody}</p>
      {/* SOWA にはオーナーがいないため、泊数返還には触れない */}
      {!isSowa && <p>{messages.unavailableOwner}</p>}
      <p className="mt-[1em] font-bold">{messages.availableHeading}</p>
      <p className="mt-[0.5em]">{messages.availableBody}</p>
    </RequiredAnswerSection>
  );
}
